import type { Aggregation, AggregationValue } from '@/types/job.types';
import { facetKeys } from './aggregations';

/**
 * Filter selection state shared by Home and Jobs.
 * Mirrors the Flutter WebHomeBloc / JobListViewBloc handlers:
 *   _OnFilterSelection, _OnClearFilter, _SetFilterBackup, _sortSelectedValuesOnTop.
 */

export type SelectedFilters = Record<string, AggregationValue[]>;

/** Flutter OtherConstants.defaultSupportedMultiFilters — the only aggregations rendered as filters. */
const DEFAULT_SUPPORTED_MULTI_FILTERS = [
  'departments',
  'city',
  'jobType',
  'skills',
  'employmentType',
];

/**
 * Aggregation names shown as filters on Home / Jobs.
 * Flutter GenericHelpers.setAllJobsSupportedFilters: tenant preference `allJobsSupportedFilters`
 * (a list of `{ key }`) replaces the defaults when present.
 */
export function getSupportedFilterKeys(preferences: Record<string, unknown> | null | undefined): string[] {
  const configured = preferences?.allJobsSupportedFilters;
  if (Array.isArray(configured) && configured.length > 0) {
    return configured
      .map((item) => (typeof item === 'string' ? item : (item as { key?: unknown })?.key))
      .filter((key): key is string => typeof key === 'string');
  }
  return DEFAULT_SUPPORTED_MULTI_FILTERS;
}

/** Flutter OtherConstants.defaultSupportedRecommendedFilters — filters on the My Jobs "Recommended" tab. */
const DEFAULT_SUPPORTED_RECOMMENDED_FILTERS = ['departments', 'locations', 'experience', 'employmentType'];

/**
 * Aggregation names shown as filters on the Recommended tab.
 * Flutter GenericHelpers.setRecommendedJobsSupportedFilters: tenant preference
 * `recommendedJobsSupportedFilters` (a list of `{ key }`) replaces the defaults when present.
 */
export function getRecommendedFilterKeys(preferences: Record<string, unknown> | null | undefined): string[] {
  const configured = preferences?.recommendedJobsSupportedFilters;
  if (Array.isArray(configured) && configured.length > 0) {
    return configured
      .map((item) => (typeof item === 'string' ? item : (item as { key?: unknown })?.key))
      .filter((key): key is string => typeof key === 'string');
  }
  return DEFAULT_SUPPORTED_RECOMMENDED_FILTERS;
}

/**
 * Selected filters as `key → values` query params (Flutter WebViewJobsBloc.selectedFilters):
 * the sort field is sent by its key, every other filter by its value keys.
 */
export function toFilterParams(selected: SelectedFilters): Record<string, string[]> {
  const params: Record<string, string[]> = {};
  for (const [key, values] of Object.entries(selected)) {
    // A merged facet value stands for every raw key it was merged from
    const keys = values
      .flatMap((v) => (key === 'selectedSortField' ? [v.additionalFields?.key ?? v.key] : facetKeys(v)))
      .filter((k): k is string => !!k);
    if (keys.length) params[key] = keys;
  }
  return params;
}

export const SORT_KEYS = ['postedOn', 'exprncFromMnth', 'relevanceScore'];
export const SORT_VALUES = ['Posting Date', 'Experience', 'Relevance'];

/**
 * Default sort filter (Flutter _SetPredefinedFilters): Posting Date without search terms,
 * Relevance with them — always descending.
 */
export function defaultSortFilters(hasSearchTerms: boolean): SelectedFilters {
  const idx = hasSearchTerms ? 2 : 0;
  return {
    selectedSortField: [
      {
        additionalFields: { sortBy: 'desc', key: SORT_KEYS[idx], displayKey: SORT_VALUES[idx] },
        catgKey: 'selectedSortField',
        key: SORT_VALUES[idx],
        displayKey: 'sortBy',
        isChecked: true,
      },
    ],
    selectedSortOrder: [{ key: 'desc', displayKey: 'desc' }],
  };
}

/**
 * Apply a filter change (Flutter _OnFilterSelection).
 * A `sortBy` value replaces the sort field and sets its order; checked values are added to
 * their category, unchecked ones removed.
 */
export function applyFilterSelection(
  selected: SelectedFilters,
  values: AggregationValue[],
): SelectedFilters {
  const next: SelectedFilters = { ...selected };
  for (const value of values) {
    let catgKey = value.catgKey ?? '';
    if (catgKey === 'sortBy') {
      catgKey = 'selectedSortField';
      const order = value.additionalFields?.sortBy ?? 'desc';
      next.selectedSortOrder = [{ key: order, displayKey: order }];
      next.selectedSortField = [{ ...value, catgKey }];
      continue;
    }
    const existing = next[catgKey] ?? [];
    if (value.isChecked) {
      if (!existing.some((v) => v.key === value.key)) {
        next[catgKey] = [...existing, { ...value, catgKey }];
      }
    } else {
      const remaining = existing.filter((v) => v.key !== value.key);
      if (remaining.length) next[catgKey] = remaining;
      else delete next[catgKey];
    }
  }
  return next;
}

/** Number of checked non-sort filter values (Flutter _OnApplyFilters appliedFilterCount). */
export function countAppliedFilters(selected: SelectedFilters): number {
  let count = 0;
  for (const [key, values] of Object.entries(selected)) {
    if (key === 'selectedSortField' || key === 'selectedSortOrder') continue;
    count += values.filter((v) => v.isChecked).length;
  }
  return count;
}

/**
 * Mark aggregation values as checked from the selected filters (Flutter _SetFilterBackup).
 * Returns new objects; value order is left unchanged so open dropdowns don't jump.
 */
export function withCheckedState(
  aggregations: Aggregation[],
  selected: SelectedFilters,
): Aggregation[] {
  const sortField = selected.selectedSortField?.[0];
  const sortOrder = selected.selectedSortOrder?.[0]?.key;

  return aggregations.map((agg) => {
    const values = agg.values ?? [];
    if (agg.name === 'sortBy') {
      return {
        ...agg,
        values: values.map((v) => {
          const isChecked = !!sortField && v.key === sortField.additionalFields?.key;
          return {
            ...v,
            isChecked,
            sortBy: isChecked ? (sortField.additionalFields?.sortBy ?? sortOrder) : v.sortBy,
          };
        }),
      };
    }
    const selectedKeys = new Set((selected[agg.name ?? ''] ?? []).map((v) => v.key));
    return { ...agg, values: values.map((v) => ({ ...v, isChecked: selectedKeys.has(v.key) })) };
  });
}

/**
 * Move the selected values of each (non-sort) aggregation to the top — Flutter
 * _sortSelectedValuesOnTop, run when search / aggregation results arrive.
 */
export function sortSelectedOnTop(
  aggregations: Aggregation[],
  selected: SelectedFilters,
): Aggregation[] {
  return aggregations.map((agg) => {
    if (agg.name === 'sortBy' || !agg.values) return agg;
    const keys = new Set((selected[agg.name ?? ''] ?? []).map((v) => v.key));
    return {
      ...agg,
      values: [
        ...agg.values.filter((v) => keys.has(v.key)),
        ...agg.values.filter((v) => !keys.has(v.key)),
      ],
    };
  });
}

/** Whether the search input has skills/roles terms (drives the default sort). */
export function hasSearchTerms(input: {
  skills?: string;
  roles?: string;
  listOfSearchSkills?: string[];
  listOfSearchRoles?: string[];
}): boolean {
  return !!(
    input.skills ||
    input.roles ||
    input.listOfSearchSkills?.length ||
    input.listOfSearchRoles?.length
  );
}
